import { useEffect, useState } from "react";
import { api, ApiError, type BodyGoal } from "../../lib/api";
import { fmtKg } from "../../lib/format";
import { BodyStat } from "./_shared";

const STATUS: Record<BodyGoal["status"], { label: string; cls: string }> = {
  reached: { label: "Reached 🎉", cls: "pill-positive" },
  ahead: { label: "Ahead of schedule", cls: "pill-positive" },
  on_track: { label: "On track", cls: "pill-neutral" },
  behind: { label: "Behind schedule", cls: "pill-warn" },
  stalled: { label: "Not trending toward goal", cls: "pill-warn" },
  no_goal: { label: "No goal set", cls: "pill-neutral" },
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function WeightGoal() {
  const [data, setData] = useState<BodyGoal | null | "error">(null);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    api.body.goal().then(setData).catch(() => setData("error"));
  }
  useEffect(load, []);

  const d = data && data !== "error" ? data : null;
  const hasTarget = d?.target_weight != null;

  function startEdit() {
    setTarget(d?.target_weight != null ? String(d.target_weight) : "");
    setDate(d?.goal?.target_date ?? "");
    setErr(null);
    setEditing(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // Preserve the non-weight goal fields (calories/protein/steps).
      await api.postGoals({
        target_weight_kg: target.trim() ? Number(target) : null,
        target_date: date.trim() || null,
        daily_calorie_target: d?.goal?.daily_calorie_target ?? null,
        daily_protein_target_g: d?.goal?.daily_protein_target_g ?? null,
        daily_steps_target: d?.goal?.daily_steps_target ?? null,
      });
      setEditing(false);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const st = d ? STATUS[d.status] : STATUS.no_goal;

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="section-title">Goal</h2>
        {!editing && (
          <button type="button" className="btn-secondary" onClick={startEdit}>
            {hasTarget ? "Edit" : "Set goal"}
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={save} className="card-pad space-y-4">
          <label className="block">
            <span className="field-label">Target weight (kg)</span>
            <input inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} className="input mt-1.5" placeholder="80" />
          </label>
          <label className="block">
            <span className="field-label">Target date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input mt-1.5" />
          </label>
          {err && <div className="text-sm text-loss">{err}</div>}
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1 py-3" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
            <button className="btn-accent flex-1 py-3" disabled={busy}>{busy ? "Saving…" : "Save goal"}</button>
          </div>
        </form>
      ) : !hasTarget ? (
        <div className="card card-pad">
          <p className="muted">Set a target weight and date to track progress, see your projected finish date, and whether you're on pace.</p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <span className={st.cls}>{st.label}</span>
            {d!.rate_kg_per_week != null && (
              <span className="text-xs text-ink-subtle tabular-nums">
                {d!.rate_kg_per_week > 0 ? "+" : ""}{d!.rate_kg_per_week} kg/week
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className="card card-pad">
            <div className="mb-1 flex justify-between text-sm">
              <span className="text-ink-subtle">{fmtKg(d!.start_weight)} start</span>
              <span className="text-ink-subtle">{fmtKg(d!.target_weight)} goal</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-ink-line/40">
              <div className="h-full rounded-full bg-accent" style={{ width: `${d!.pct_complete ?? 0}%` }} />
            </div>
            <div className="mt-1.5 text-center text-sm">
              <span className="font-display text-lg tabular-nums text-accent">{d!.pct_complete ?? 0}%</span>
              <span className="text-ink-subtle"> · now {fmtKg(d!.current_weight)}</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <BodyStat label="To go" value={d!.to_go != null ? Math.abs(d!.to_go).toFixed(1) : null} unit="kg" accent />
            <BodyStat label="Projected" value={d!.projected_date ? fmtDate(d!.projected_date) : "—"} />
            <BodyStat label="Target date" value={d!.goal?.target_date ? fmtDate(d!.goal.target_date) : "—"} />
          </div>

          {d!.status === "stalled" && (
            <p className="mt-3 text-xs text-ink-subtle">
              Your recent weigh-ins aren't trending toward the target yet — no projection until the
              7-day average starts moving the right way.
            </p>
          )}
        </>
      )}
    </div>
  );
}
